import React from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Icon,
  Banner,
} from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';

interface ErrorMessageProps {
  title?: string;
  message?: string;
  error?: Error | string | null;
  onRetry?: () => void;
  onRefresh?: () => void;
  variant?: 'card' | 'banner' | 'inline';
  showDetails?: boolean;
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
  title = 'Unable to load content',
  message = 'We encountered an issue while loading your content. This might be due to a network connection problem or a temporary service issue.',
  error,
  onRetry,
  onRefresh,
  variant = 'card',
  showDetails = false,
}) => {
  const [showErrorDetails, setShowErrorDetails] = React.useState(showDetails);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      window.location.reload();
    }
  };

  const toggleDetails = () => {
    setShowErrorDetails(!showErrorDetails);
  };

  const errorContent = (
    <BlockStack gap="400">
      {/* Error Icon */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        backgroundColor: '#fef7f0',
        margin: '0 auto'
      }}>
        <Icon source={AlertTriangleIcon} tone="critical" />
      </div>

      {/* Error Heading */}
      <Text variant="headingLg" as="h1" tone="critical">
        {title}
      </Text>

      {/* Error Description */}
      <Text variant="bodyMd" as="p" tone="subdued">
        {message}
      </Text>

      {/* Action Buttons */}
      <InlineStack gap="300" align="center">
        <Button
          variant="primary"
          onClick={handleRetry}
          tone="success"
        >
          Try Again
        </Button>
        <Button
          onClick={handleRefresh}
          variant="secondary"
        >
          Refresh Page
        </Button>
      </InlineStack>

      {/* Error Details (Development) */}
      {process.env.NODE_ENV === 'development' && error && (
        <div style={{
          backgroundColor: '#f6f6f7',
          borderRadius: '8px',
          padding: '1rem',
          marginTop: '1rem'
        }}>
          <Button
            variant="tertiary"
            onClick={toggleDetails}
            icon={showErrorDetails ? 'ChevronDownIcon' : 'ChevronRightIcon'}
          >
            Error Details (Development)
          </Button>
          
          {showErrorDetails && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#ffffff',
              borderRadius: '4px',
              border: '1px solid #e1e3e5',
              fontFamily: 'monospace',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '200px'
            }}>
              <Text variant="bodySm" as="p" fontWeight="semibold">
                Error: {typeof error === 'string' ? error : error.message}
              </Text>
              {typeof error !== 'string' && error.stack && (
                <Text variant="bodySm" as="p" tone="subdued">
                  Stack: {error.stack}
                </Text>
              )}
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <Text variant="bodySm" as="p" tone="subdued">
        Need help? If this problem persists, please contact support or check your network connection.
      </Text>
    </BlockStack>
  );

  if (variant === 'banner') {
    return (
      <Banner
        title={title}
        tone="critical"
        action={{ content: 'Try Again', onAction: handleRetry }}
      >
        <p>{message}</p>
      </Banner>
    );
  }

  if (variant === 'inline') {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        {errorContent}
      </div>
    );
  }

  // Default card variant
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f6f6f7',
      padding: '20px'
    }}>
      <div style={{ maxWidth: '500px', width: '100%' }}>
        <Card>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            {errorContent}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ErrorMessage;
